import React from "react";
import { Star, X } from "lucide-react";
import { capitalizeFirstLetter } from "../../utils/stringUtils";
import bookedStamp from "../../assets/booked.png";
import cancelledStampImage from "../../assets/cancelledStampImage.png";

const ProposalCard = ({
  proposal,
  isProfessionalView = false,
  onViewDetails,
  onWithdraw,
  hostName,
  hostAvatar,
  hostRating,
  hostReviews,
  showCheckbox = false,
  isSelected = false,
  onToggleSelect,
  onReject,
  isBooked = false,
  isCancelled = false,
  customWidth = "",
}) => {
  const manager = proposal.managerId || {};
  const portfolio = manager.portfolio || {};

  const statusStr = String(proposal?.status || "").toLowerCase();
  const lifecycleStr = String(
    proposal?.lifecycleStatus ||
    proposal?.bookingDoc?.lifecycleStatus ||
    (typeof proposal?.bookingId === "object" ? proposal?.bookingId?.lifecycleStatus : "") ||
    ""
  ).toUpperCase();
  const bookingStatusStr = String(
    proposal?.bookingStatus ||
    proposal?.bookingDoc?.bookingStatus ||
    (typeof proposal?.bookingId === "object" ? proposal?.bookingId?.bookingStatus : "") ||
    ""
  ).toUpperCase();

  const isWithdrawn = Boolean(statusStr === "withdrawn" || proposal?.status === "withdrawn");
  const isRejected = Boolean(statusStr === "rejected" || proposal?.status === "rejected");
  const isProposalCancelled = !isRejected && !isWithdrawn && Boolean(
    isCancelled ||
    proposal?.isCancelled ||
    statusStr === "cancelled" ||
    lifecycleStr === "CANCELLED" ||
    bookingStatusStr === "CANCELLED"
  );
  const isProposalBooked = Boolean(
    !isProposalCancelled &&
    !isRejected &&
    !isWithdrawn &&
    (
      (isBooked && !isCancelled) ||
      proposal?.isBooked ||
      statusStr === "booked" ||
      (Boolean(proposal?.bookingId || proposal?.bookingDoc) && lifecycleStr !== "CANCELLED")
    )
  );

  const formatBudget = () => {
    const source = proposal.quoteAmount != null ? proposal.quoteAmount : proposal.quote;
    if (source == null) return "N/A";
    let amount = typeof source === "object" ? source.amount : source;
    if (typeof amount !== "number") return "Invalid budget";
    return amount.toLocaleString("en-IN");
  };

  const name = capitalizeFirstLetter(hostName || proposal.companyName || proposal.plannerName || manager.fullName || "Elegant Events");

  const avgRating = (hostRating != null ? hostRating : null) ?? (proposal.avgRating != null ? proposal.avgRating : null) ?? (portfolio.ratings != null ? portfolio.ratings : null) ?? (manager.ratings != null ? manager.ratings : null) ?? 0;
  const formattedRating = parseFloat(avgRating || 0).toFixed(1);
  const reviewCount = (hostReviews != null ? hostReviews : null) ?? (proposal.totalReviews != null ? proposal.totalReviews : null) ?? (portfolio.reviews != null ? portfolio.reviews : null) ?? (manager.reviews != null ? manager.reviews : null) ?? 0;
  const hasReviews = Number(reviewCount) > 0 && Number(avgRating) > 0;

  // Use professional's profile picture or generated avatar
  const rawAvatar = hostAvatar || proposal.profilePhoto || manager.profilePicture || manager.profilePhoto || portfolio.profilePicture || null;
  const avatar = typeof rawAvatar === 'object' && rawAvatar !== null ? rawAvatar.url : rawAvatar;
  const eventImg = avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&size=200&background=random`;

  const description = proposal.proposalDetail || proposal.proposalDescription || proposal.additionalDetails || "We are excited to create a very memorable birthday experience for you.....";

  // A proposal is withdrawable when it is pending or not_verified and not already booked
  const isWithdrawable = isProfessionalView && !isWithdrawn && !proposal.bookingId &&
    ["pending", "not_verified"].includes(proposal.status);

  return (
    <div className={`min-w-0 ${customWidth || 'w-full max-w-[360px]'} bg-white rounded-[22px] sm:rounded-[24px] shadow-[0_8px_30px_rgb(0,0,0,0.06)] border-2 group relative transition-all ${isSelected ? 'border-[#FF4D12]' : 'border-gray-100'}`}>
      {/* Booked Stamp Overlay — Positioned relative to the card container at the top left */}
      {isProposalBooked && (
        <img
          src={bookedStamp}
          alt="Booked stamp"
          className="absolute top-[-20px] left-[-20px] sm:top-[-24px] sm:left-[-24px] md:top-[-28px] md:left-[-28px] w-24 h-24 sm:w-26 sm:h-26 md:w-28 md:h-28 rotate-[-20deg] opacity-100 drop-shadow-[0_4px_8px_rgba(0,0,0,0.18)] select-none pointer-events-none z-30"
        />
      )}

      {/* Cancelled Stamp Overlay — Positioned relative to the card container at the top left */}
      {isProposalCancelled && (
        <img
          src={cancelledStampImage}
          alt="Cancelled stamp"
          className="absolute top-[-20px] left-[-20px] sm:top-[-24px] sm:left-[-24px] md:top-[-28px] md:left-[-28px] w-24 h-24 sm:w-26 sm:h-26 md:w-28 md:h-28 rotate-[-20deg] opacity-100 drop-shadow-[0_4px_8px_rgba(0,0,0,0.18)] select-none pointer-events-none z-30"
        />
      )}

      {/* Rejected Tag Overlay — top left red tag */}
      {isRejected && (
        <div className="absolute top-2.5 left-2.5 z-30 bg-[#FF1717] text-white text-[10px] sm:text-[11px] font-bold px-2.5 py-1 rounded-md shadow-sm uppercase tracking-wide select-none pointer-events-none">
          Rejected
        </div>
      )}

      {/* Checkbox Selection Overlay — top left corner */}
      {showCheckbox && onToggleSelect && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onToggleSelect(proposal);
          }}
          className={`absolute top-2.5 left-2.5 z-30 w-6 h-6 rounded-md flex items-center justify-center border transition-all ${isSelected
              ? "bg-[#FF4D12] border-[#FF4D12] text-white shadow-sm"
              : "bg-white border-gray-300 hover:border-[#FF4D12]"
            }`}
        >
          {isSelected && (
            <svg className="w-3.5 h-3.5 fill-none stroke-current" viewBox="0 0 24 24" strokeWidth="3">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          )}
        </button>
      )}

      {/* Reject button — top right corner, client view only */}
      {!isProfessionalView && proposal.status !== "rejected" && onReject && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onReject(proposal._id);
          }}
          title="Reject proposal"
          className="absolute top-2.5 right-2.5 z-30 w-7 h-7 bg-[#FF1717] hover:bg-red-600 rounded-lg flex items-center justify-center text-[#FF1717] hover:text-white transition-all shadow-md active:scale-95"
        >
          <X className="w-4 h-4 text-white" strokeWidth={3} />
        </button>
      )}

      {/* Withdraw button — top right corner, professional view only */}
      {isWithdrawable && onWithdraw && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onWithdraw(proposal);
          }}
          title="Withdraw proposal"
          className="absolute top-4 right-4 z-20 w-7 h-7 bg-[#FF1717] hover:bg-red-600 rounded-lg flex items-center justify-center text-white transition-all shadow-md active:scale-95"
        >
          <X className="w-4 h-4" strokeWidth={3} />
        </button>
      )}

      {/* Withdrawn badge */}
      {isWithdrawn && (
        <div className="absolute top-3 left-3 z-20 bg-gray-600 text-white text-[10px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wide">
          Withdrawn
        </div>
      )}

      <div
        onClick={(e) => {
          e.stopPropagation();
          if (onViewDetails) onViewDetails(proposal);
        }}
        className={`aspect-square overflow-hidden relative m-2 sm:m-2.5 md:m-3 rounded-[15px] sm:rounded-[17px] md:rounded-[19px] cursor-pointer ${isWithdrawn ? "opacity-50 grayscale" : ""}`}
      >
        <img
          src={eventImg}
          alt="Proposal"
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
      </div>

      <div className={`min-w-0 px-2 sm:px-2.5 md:px-3.5 py-1 sm:py-1.5 md:py-2 ${isWithdrawn ? "opacity-60" : ""}`}>
        <div className="flex items-center justify-between mb-1 sm:mb-1.5 gap-1 min-w-0 w-full">
          <h4 
            className="font-bold text-gray-900 text-[12px] sm:text-[13.5px] md:text-[15px] lg:text-[16px] truncate leading-tight flex-1 min-w-0"
            title={name}
          >
            {name}
          </h4>
          {hasReviews && (
            <div className="flex items-center gap-0.5 sm:gap-1 shrink-0 whitespace-nowrap ml-auto">
              <Star className="w-2.5 h-2.5 sm:w-3 sm:h-3 md:w-3.5 md:h-3.5 fill-[#FF4D12] text-[#FF4D12] shrink-0" />
              <span className="text-[10px] sm:text-[11px] md:text-[12px] text-gray-700 font-semibold whitespace-nowrap">
                {formattedRating}
              </span>
            </div>
          )}
        </div>

        <p className="text-gray-500 text-[10px] sm:text-[11px] md:text-[12px] mb-1.5 sm:mb-2 line-clamp-2 leading-relaxed">
          {description}
        </p>

        <hr className="border-gray-100 mb-1.5 sm:mb-2" />

        <div className="flex items-end justify-between pb-1 sm:pb-1.5 md:pb-2 gap-1 sm:gap-1.5 md:gap-2">
          <div className="flex flex-col min-w-0">
            <span className="text-[9px] sm:text-[10px] md:text-[11px] text-gray-500 mb-0.5">Quote</span>
            {(() => {
              const budgetText = formatBudget();
              const len = String(budgetText || "").length;
              let sizeClass = "text-[12px] sm:text-[13.5px] md:text-[15px] lg:text-[16.5px]";
              if (len >= 11) sizeClass = "text-[9.5px] sm:text-[11px] md:text-[12px] lg:text-[13px]";
              else if (len >= 8) sizeClass = "text-[10.5px] sm:text-[12px] md:text-[13.5px] lg:text-[14.5px]";
              return (
                <span className={`font-extrabold text-black tracking-tight whitespace-nowrap leading-none ${sizeClass}`}>
                  {budgetText}
                </span>
              );
            })()}
          </div>
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (onViewDetails) onViewDetails(proposal);
            }}
            className="bg-[#FF4D12] hover:bg-[#e64510] text-white text-[9.5px] sm:text-[10.5px] md:text-[11.5px] lg:text-[12.5px] px-2 py-1 sm:px-2.5 sm:py-1.5 md:px-3 md:py-1.5 rounded-[6px] sm:rounded-[7px] md:rounded-[8px] font-bold transition-all shadow-sm active:scale-95 whitespace-nowrap tracking-tight shrink-0"
          >
            View Details
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProposalCard;
